import { SupabaseClient } from '@supabase/supabase-js'

export interface MCPConnection {
  id: string
  project_id: string
  name: string
  description?: string
  connection_type: 'stdio' | 'http' | 'sse'
  server_url?: string
  transport_config?: any
  auth_type: 'none' | 'api_key' | 'bearer' | 'basic'
  auth_credentials?: any
  is_active: boolean
  last_connected_at?: string
  connection_status: 'connected' | 'disconnected' | 'error'
  last_error?: string
  created_by: string
  created_at: string
  updated_at: string
}

export interface MCPTool {
  id: string
  connection_id: string
  name: string
  description?: string
  input_schema: any
  is_enabled: boolean
  last_used_at?: string
  usage_count: number
  discovered_at: string
  last_synced_at: string
}

export interface CreateMCPConnectionDTO {
  name: string
  description?: string
  connection_type: 'stdio' | 'http' | 'sse'
  server_url?: string
  transport_config?: any
  auth_type: 'none' | 'api_key' | 'bearer' | 'basic'
  auth_credentials?: any
}

export interface UpdateMCPConnectionDTO extends Partial<CreateMCPConnectionDTO> {
  is_active?: boolean
}

export class MCPConnectionService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * List all MCP connections for a project
   */
  async listConnections(projectId: string): Promise<MCPConnection[]> {
    const { data, error } = await this.supabase
      .from('mcp_connections')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data || []
  }

  /**
   * Get a single MCP connection
   */
  async getConnection(connectionId: string): Promise<MCPConnection | null> {
    const { data, error } = await this.supabase
      .from('mcp_connections')
      .select('*')
      .eq('id', connectionId)
      .single()

    if (error) throw error
    return data
  }

  /**
   * Create a new MCP connection
   */
  async createConnection(
    projectId: string,
    userId: string,
    connection: CreateMCPConnectionDTO
  ): Promise<MCPConnection> {
    const { data, error } = await this.supabase
      .from('mcp_connections')
      .insert({
        ...connection,
        project_id: projectId,
        created_by: userId
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Update an MCP connection
   */
  async updateConnection(
    connectionId: string,
    updates: UpdateMCPConnectionDTO
  ): Promise<MCPConnection> {
    const { data, error } = await this.supabase
      .from('mcp_connections')
      .update(updates)
      .eq('id', connectionId)
      .select()
      .single()

    if (error) throw error
    return data
  }

  /**
   * Delete an MCP connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    const { error } = await this.supabase
      .from('mcp_connections')
      .delete()
      .eq('id', connectionId)

    if (error) throw error
  }

  /**
   * Update connection status
   */
  async updateConnectionStatus(
    connectionId: string,
    status: 'connected' | 'disconnected' | 'error',
    error?: string
  ): Promise<void> {
    const updates: any = {
      connection_status: status,
      last_error: error || null
    }

    if (status === 'connected') {
      updates.last_connected_at = new Date().toISOString()
    }

    const { error: updateError } = await this.supabase
      .from('mcp_connections')
      .update(updates)
      .eq('id', connectionId)

    if (updateError) throw updateError
  }

  /**
   * List tools for a connection
   */
  async listTools(connectionId: string): Promise<MCPTool[]> {
    const { data, error } = await this.supabase
      .from('mcp_tools')
      .select('*')
      .eq('connection_id', connectionId)
      .order('name', { ascending: true })

    if (error) throw error
    return data || []
  }

  /**
   * Sync tools from MCP server
   */
  async syncTools(connectionId: string, tools: Array<{
    name: string
    description?: string
    inputSchema: any
  }>): Promise<MCPTool[]> {
    // Get existing tools
    const existingTools = await this.listTools(connectionId)
    const existingToolNames = new Set(existingTools.map(t => t.name))

    // Prepare tools to insert/update
    const toolsToUpsert = tools.map(tool => ({
      connection_id: connectionId,
      name: tool.name,
      description: tool.description,
      input_schema: tool.inputSchema,
      last_synced_at: new Date().toISOString()
    }))

    // Upsert tools
    const { data: upsertedTools, error: upsertError } = await this.supabase
      .from('mcp_tools')
      .upsert(toolsToUpsert, {
        onConflict: 'connection_id,name',
        ignoreDuplicates: false
      })
      .select()

    if (upsertError) throw upsertError

    // Delete tools that no longer exist
    const newToolNames = new Set(tools.map(t => t.name))
    const toolsToDelete = existingTools
      .filter(t => !newToolNames.has(t.name))
      .map(t => t.id)

    if (toolsToDelete.length > 0) {
      const { error: deleteError } = await this.supabase
        .from('mcp_tools')
        .delete()
        .in('id', toolsToDelete)

      if (deleteError) throw deleteError
    }

    return upsertedTools || []
  }

  /**
   * Toggle tool enabled status
   */
  async toggleToolEnabled(toolId: string, enabled: boolean): Promise<void> {
    const { error } = await this.supabase
      .from('mcp_tools')
      .update({ is_enabled: enabled })
      .eq('id', toolId)

    if (error) throw error
  }

  /**
   * Log tool execution
   */
  async logToolExecution(
    toolId: string,
    projectId: string,
    userId: string,
    execution: {
      roomId?: string
      chatId?: string
      inputParameters: any
      outputResult?: any
      errorMessage?: string
      status: 'pending' | 'success' | 'error' | 'timeout'
      durationMs?: number
    }
  ): Promise<void> {
    const { error } = await this.supabase
      .from('mcp_tool_executions')
      .insert({
        tool_id: toolId,
        project_id: projectId,
        executed_by: userId,
        room_id: execution.roomId,
        chat_id: execution.chatId,
        input_parameters: execution.inputParameters,
        output_result: execution.outputResult,
        error_message: execution.errorMessage,
        status: execution.status,
        duration_ms: execution.durationMs,
        completed_at: execution.status !== 'pending' ? new Date().toISOString() : null
      })

    if (error) throw error

    // Update tool usage count and last used timestamp
    if (execution.status === 'success') {
      const { error: updateError } = await this.supabase
        .from('mcp_tools')
        .update({
          usage_count: this.supabase.raw('usage_count + 1'),
          last_used_at: new Date().toISOString()
        })
        .eq('id', toolId)

      if (updateError) throw updateError
    }
  }
}